import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  message,
  Row,
  Col,
  Input,
  Drawer,
  Form,
  Select,
  Switch,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { askQuestionAPIWithErrorHandling, askQuestionHelpers } from "../../../api/ask-question/ask-question";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const { Paragraph } = Typography;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["status"];

export default function AskQuestionTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchData = async (page = 1, pageSize = 15, search = "", status = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: search.trim(),
        status: status,
      };
      const response = await askQuestionAPIWithErrorHandling.getAskQuestions(params);
      const { questions, pagination: paginationData } = askQuestionHelpers.extractAskQuestionList(response);

      setData(questions);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách câu hỏi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchData(paginationConfig.current, paginationConfig.pageSize, searchText, statusFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchData(1, pagination.pageSize, value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchData(1, pagination.pageSize, searchText, value);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    fetchData(1, pagination.pageSize, "", "");
  };

  const handleToggleStatus = async (id) => {
    try {
      await askQuestionAPIWithErrorHandling.toggleStatusAskQuestion(id);
      message.success("Cập nhật trạng thái thành công");
      fetchData(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    const confirmed = await confirmDelete({ entity: "câu hỏi" });
    if (!confirmed) return;

    try {
      await askQuestionAPIWithErrorHandling.deleteAskQuestion(record.id);
      message.success("Xóa câu hỏi thành công");
      fetchData(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const showModal = (record = null) => {
    setSelectedRecord(record);
    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true, position: 0 });
    }
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (selectedRecord) {
        await askQuestionAPIWithErrorHandling.updateAskQuestion(selectedRecord.id, values);
        message.success("Cập nhật câu hỏi thành công");
      } else {
        await askQuestionAPIWithErrorHandling.storeAskQuestion(values);
        message.success("Thêm câu hỏi thành công");
      }
      setIsModalOpen(false);
      fetchData(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Lỗi xử lý yêu cầu");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "content", label: "Câu hỏi & Trả lời", fixed: true,
      column: {
        title: "Câu hỏi & Trả lời",
        key: "content",
        render: (_, record) => (
          <div className="py-2">
            <div className="flex items-start gap-2 mb-2">
              <Tag color="blue" className="mt-1">Hỏi</Tag>
              <span className="font-semibold text-gray-800">{record.question}</span>
            </div>
            <div className="flex items-start gap-2">
              <Tag color="green" className="mt-1">Đáp</Tag>
              <div className="text-gray-600 text-sm">
                <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'Xem thêm' }}>
                  {record.answer}
                </Paragraph>
              </div>
            </div>
          </div>
        ),
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status, record) => (
          <Switch
            size="small"
            checked={status}
            onChange={() => handleToggleStatus(record.id)}
          />
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm nội dung câu hỏi..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc trạng thái"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          allowClear
        >
          <Select.Option value="true">Đang hiển thị</Select.Option>
          <Select.Option value="false">Đang ẩn</Select.Option>
        </Select>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter);

  return (
    <BrandedPageLayout
      title="Hỏi đáp"
      subtitle="Quản lý danh sách câu hỏi thường gặp (FAQ)."
      breadcrumbItems={[{ title: "Hỏi đáp" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchData(pagination.current, pagination.pageSize, searchText, statusFilter)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm câu hỏi
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={
          <div className="flex items-center gap-2">
            {selectedRecord ? <EditOutlined className="text-blue-500" /> : <PlusOutlined className="text-green-500" />}
            <span>{selectedRecord ? "Chỉnh sửa câu hỏi" : "Thêm câu hỏi mới"}</span>
          </div>
        }
        placement="right"
        width="min(720px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleModalSubmit}>
              {selectedRecord ? "Cập nhật" : "Thêm mới"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="mt-4 pt-4 border-t">
          <Form.Item
            name="question"
            label="Nội dung câu hỏi"
            rules={[{ required: true, message: "Vui lòng nhập câu hỏi!" }]}
          >
            <Input.TextArea rows={3} placeholder="Ví dụ: Đăng ký kết hôn cần những giấy tờ gì?" />
          </Form.Item>

          <Form.Item
            name="answer"
            label="Nội dung câu trả lời"
            rules={[{ required: true, message: "Vui lòng nhập câu trả lời!" }]}
          >
            <Input.TextArea rows={6} placeholder="Nhập hướng dẫn chi tiết..." />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Vị trí sắp xếp"
                tooltip="Số nhỏ hơn sẽ hiển thị trước"
              >
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
